<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi, RowNode } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { theme } from '../../../services/stores/theme-store.js';
  import { projectStructureStore, studyExtractStore } from './stores.js';
  import { userStore } from '../../../services/stores/user-store.js';
  import { updateReferenceCell } from './target-table-processing.js';
  import { saveTargetExtract } from '../data/target-data-handler.js';
  import { ColumnHeaderForTarget } from './headerComponents.js';
  import { getCellEditorConfig,  getLookupValueGetterSetter,  getLookupCellRenderer } from './cell-editor-config.js';
  import { createCellWithIndicator, DEFAULT_CURRENT_DOCUMENT_ID } from '../indicator-utils.js';
  import { logInfo, logError, logWarning } from "../../../services/logging/log-handler.js";
  
  // Props
  export let columns: any[] = []; // Column structure from project structure
  export let rows: any[] = []; // Row data from extract store
  export let data2D: any[][] | null = null; // Alternative: 2D array format (headers + rows)
  export let columnStructure: any[] | null = null; // Optional: Column structure when using data2D
  export let rowIdMap: Map<number, string> | null = null; // Optional: Map of rowIndex -> rowId when using data2D
  export let referenceId: string = '';
  export let showColumnKeys: boolean = true;
  export let onCellValueChanged: ((params: any) => void) | null = null; // Optional callback for cell changes
  export let getSourceInfoForCell: ((rowIndex: number, columnId: string) => any) | null = null; // Optional function to get source info
  export let currentDocumentID: string = DEFAULT_CURRENT_DOCUMENT_ID;
  export let showIndicators: boolean = true; // Whether to show source indicators in cells
  export let domLayout: 'normal' | 'autoHeight' = 'autoHeight'; // Grid layout: 'normal' for internal scrolling, 'autoHeight' for content-based height
  export let enableRowSelection: boolean = false; // Whether to enable row selection with checkboxes
  export let onSelectionChanged: ((selectedRows: Set<number>) => void) | null = null; // Callback for selection changes
  export let onGridReady: ((api: GridApi) => void) | null = null; // Callback when grid is ready
  
  // Grid refs
  let gridDiv: HTMLDivElement;
  let gridApi: GridApi | null = null;
  let gridInstance: any = null;
  
  // Track if grid has been initialized to prevent unnecessary reinitializations
  let gridInitialized = false;
  let lastColumnsLength = 0;
  let lastRowsLength = 0;
  let lastData2DLength = 0;
  let lastColumnStructureLength = 0;
  let lastReferenceId = '';
  
  // Convert columns/rows to columnDefs and rowData
  function createColumnDefsAndRowData(): { columnDefs: any[], rowData: any[] } {
    
    // If data2D is provided, convert it to columns/rows format first
    if (data2D && data2D.length > 0) {
      const headers = data2D[0];
      const dataRows = data2D.slice(1);
      
      
      // Use columnStructure if provided, otherwise create simple structure
      const convertedColumns = columnStructure && columnStructure.length > 0 
        ? columnStructure 
        : headers.map((header: string) => ({
            id: header.toLowerCase().replace(/\s+/g, '_'),
            label: header
          }));
      
      
      // Convert to row structure
      const convertedRows = dataRows.map((row: any[], index: number) => {
        // Use rowIdMap if provided, otherwise generate ID
        const rowId = rowIdMap?.get(index) || `r${index + 1}`;
        const rowObj: any = {
          id: rowId,
          cells: row.map((value: any, colIndex: number) => {
            const col = convertedColumns[colIndex];
            return {
              id: col?.id || headers[colIndex]?.toLowerCase().replace(/\s+/g, '_') || `col${colIndex}`,
              value: value || '-'
            };
          })
        };
        return rowObj;
      });
      
      
      return createColumnDefsAndRowDataFromStructure(convertedColumns, convertedRows);
    }
    
    // Use columns/rows structure
    
    if (columns.length === 0) {
      logWarning('ReferenceGrid: No columns provided', "ReferenceGrid", undefined);
      return { columnDefs: [], rowData: [] };
    }
    
    return createColumnDefsAndRowDataFromStructure(columns, rows);
  }
  
  function createColumnDefsAndRowDataFromStructure(cols: any[], rowData: any[]): { columnDefs: any[], rowData: any[] } {
    
    const columnDefs = createColumnDefs(cols);
    const agGridRowData = createRowData(rowData, cols);
    
    return { columnDefs, rowData: agGridRowData };
  }
  
  function createColumnDefs(cols: any[]): any[] {
    const columnDefs: any[] = [];
    
    cols.forEach((column, index) => {
      // Don't pin first column when row selection is enabled (checkbox column should be first)
      const isFirstColumn = index === 0;
      const isFixed = isFirstColumn && !enableRowSelection;
      const isMultiline = column.multiline === true;
      
      // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
      if (column.type === 'group' && Array.isArray(column.fields)) {
        const groupColumns = column.fields.map((field: any) => {
          const fieldMultiline = field.multiline === true || field.type === 'multiline';
          const editorConfig = getCellEditorConfig(field, $projectStructureStore);
          const lookupValueConfig = getLookupValueGetterSetter(field, $projectStructureStore);
          const lookupRenderer = getLookupCellRenderer(field, $projectStructureStore);
          
          return {
            field: field.id,
            headerName: field.label || field.id,
            headerComponent: 'columnHeaderForTarget',
            headerComponentParams: {
              displayName: field.label || field.id,
              key: field.id,
              showColumnKeys: showColumnKeys
            },
            pinned: undefined,
            width: fieldMultiline ? 200 : undefined,
            minWidth: fieldMultiline ? 200 : (showIndicators ? 100 : 80),
            maxWidth: fieldMultiline ? 200 : undefined,
            editable: true,
            ...editorConfig,
            wrapText: fieldMultiline,
            autoHeight: domLayout === 'autoHeight' && fieldMultiline,
            suppressSizeToFit: fieldMultiline,
            suppressAutoSize: fieldMultiline,
            sortable: false,
            filter: false,
            resizable: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            valueGetter: (params: any) => {
              const value = params.data[field.id];
              if (value === '-' || value === undefined || value === null) return '';
              if (lookupValueConfig?.valueGetter) {
                return lookupValueConfig.valueGetter(params);
              }
              return value;
            },
            valueSetter: (params: any) => {
              let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
              if (lookupValueConfig?.valueSetter) {
                return lookupValueConfig.valueSetter(params);
              }
              params.data[field.id] = value;
              return true;
            },
            cellRenderer: (params: any) => {
              let displayValue = params.value || '-';
              if (lookupRenderer && displayValue !== '-') {
                displayValue = lookupRenderer(params) || '-';
              }
              const sourceInfo = params.data?.[`${field.id}_sourceInfo`];
              if (getSourceInfoForCell) {
                const additionalSourceInfo = getSourceInfoForCell(params.node.rowIndex, field.id);
                return showIndicators 
                  ? createCellWithIndicator(displayValue, additionalSourceInfo || sourceInfo, currentDocumentID)
                  : displayValue;
              }
              return showIndicators 
                ? createCellWithIndicator(displayValue, sourceInfo, currentDocumentID)
                : displayValue;
            },
            cellStyle: fieldMultiline ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            } : {
              'white-space': 'nowrap',
              'overflow': 'hidden',
              'text-overflow': 'ellipsis',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            }
          };
        });

        if (groupColumns.length > 0) {
          columnDefs.push({
            headerName: column.label || column.id,
            marryChildren: true,
            children: groupColumns,
            resizable: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          });
        }
      } else {
        // Handle regular columns
        const editorConfig = getCellEditorConfig(column, $projectStructureStore);
        const lookupValueConfig = getLookupValueGetterSetter(column, $projectStructureStore);
        const lookupRenderer = getLookupCellRenderer(column, $projectStructureStore);
        
        const columnDef: any = {
          field: column.id,
          headerName: column.label || column.id,
          headerComponent: 'columnHeaderForTarget',
          headerComponentParams: {
            displayName: column.label || column.id,
            key: column.id,
            showColumnKeys: showColumnKeys
          },
          pinned: isFixed ? 'left' : undefined,
          width: isMultiline ? 200 : (index === 0 ? 200 : undefined),
          minWidth: isMultiline ? 200 : (index === 0 ? 200 : showIndicators ? 100 : 80), // Increase minWidth when indicators are shown
          maxWidth: isMultiline ? 200 : (index === 0 ? 200 : undefined),
          editable: true, // All columns are editable for references
          ...editorConfig,
          wrapText: isMultiline, // Only wrap text for multiline columns
          autoHeight: domLayout === 'autoHeight', // Only use column autoHeight with autoHeight layout
          suppressSizeToFit: isFixed,
          suppressAutoSize: isFixed,
          sortable: false,
          filter: false,
          resizable: false,
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          valueGetter: (params: any) => {
            const value = params.data[column.id];
            if (value === '-' || value === undefined || value === null) return '';
            if (lookupValueConfig?.valueGetter) {
              return lookupValueConfig.valueGetter(params);
            }
            return value;
          },
          valueSetter: (params: any) => {
            let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
            if (lookupValueConfig?.valueSetter) {
              return lookupValueConfig.valueSetter(params);
            }
            params.data[column.id] = value;
            return true;
          },
          cellRenderer: (params: any) => {
            let displayValue = params.value || '-';
            if (lookupRenderer && displayValue !== '-') {
              displayValue = lookupRenderer(params) || '-';
            }
            const sourceInfo = params.data?.[`${column.id}_sourceInfo`];
            if (getSourceInfoForCell) {
              const additionalSourceInfo = getSourceInfoForCell(params.node.rowIndex, column.id);
              return showIndicators 
                ? createCellWithIndicator(displayValue, additionalSourceInfo || sourceInfo, currentDocumentID)
                : displayValue;
            }
            return showIndicators 
              ? createCellWithIndicator(displayValue, sourceInfo, currentDocumentID)
              : displayValue;
          },
          cellStyle: isMultiline || isFixed ? {
            'white-space': 'normal',
            'word-wrap': 'break-word',
            'overflow-wrap': 'break-word',
            'padding': '8px 8px',
            'line-height': 'normal',
            'vertical-align': 'top'
          } : {
            'white-space': 'nowrap', // Prevent word splitting for non-multiline columns
            'overflow': 'hidden',
            'text-overflow': 'ellipsis',
            'padding': '8px 8px',
            'line-height': 'normal',
            'vertical-align': 'top'
          }
        };
        
        columnDefs.push(columnDef);
      }
    });
    
    return columnDefs;
  }
  
  function createRowData(rowData: any[], cols: any[]): any[] {
    return rowData.map(row => {
      const rowObj: any = { id: row.id };
      
      cols.forEach(column => {
        // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
        if (column.type === 'group' && Array.isArray(column.fields)) {
          // For grouped columns, map cells by field IDs
          column.fields.forEach((field: any) => {
            const cell = row.cells?.find((c: any) => c.id === field.id);
            rowObj[field.id] = cell?.value || '-';
            if (cell?.sourceInfo) {
              rowObj[`${field.id}_sourceInfo`] = cell.sourceInfo;
            }
          });
        } else {
          // Handle regular columns
          const cell = row.cells?.find((c: any) => c.id === column.id);
          rowObj[column.id] = cell?.value || '-';
          if (cell?.sourceInfo) {
            rowObj[`${column.id}_sourceInfo`] = cell.sourceInfo;
          }
        }
      });
      
      return rowObj;
    });
  }
  
  function initializeGrid() {
    if (!gridDiv) {
      return;
    }
    
    // Destroy existing grid only if we have a valid instance
    if (gridInstance) {
      try {
        gridInstance.destroy();
      } catch (error) {
        logError(error, "ReferenceGrid", undefined, { message: 'ReferenceGrid: Error destroying grid instance' });
      }
      gridInstance = null;
      gridApi = null;
      // Don't reset gridInitialized here - we're recreating immediately, so keep it true
      // to prevent reactive statement from retriggering
    }
    
    const { columnDefs, rowData } = createColumnDefsAndRowData();
    
    if (columnDefs.length === 0) {
      logWarning('ReferenceGrid: No column definitions created, skipping grid initialization', "ReferenceGrid", undefined);
      return;
    }
    
    if (rowData.length === 0) {
      logWarning('ReferenceGrid: No row data, but initializing grid with empty data', "ReferenceGrid", undefined);
    }
    
    // Calculate row height for multiline cells
    const getRowHeight = (params: any) => {
      let maxHeight = 32; // Base row height
      
      // Check each column for multiline content
      columnDefs.forEach((colDef: any) => {
        // For autoHeight layout, use colDef.autoHeight; for normal layout, check if column has multiline styling
        let isMultilineColumn = false;
        if (domLayout === 'autoHeight') {
          isMultilineColumn = colDef.autoHeight === true;
        } else {
          // For normal layout, check if column has multiline cell styling
          const cellStyle = colDef.cellStyle;
          isMultilineColumn = cellStyle && (
            cellStyle['white-space'] === 'normal' || 
            cellStyle['word-wrap'] === 'break-word' ||
            cellStyle['overflow-wrap'] === 'break-word'
          );
        }
        
        if (isMultilineColumn && params.data && colDef.field) {
          const value = params.data[colDef.field];
          if (value && value !== '-' && typeof value === 'string') {
            // Estimate height based on content length
            const columnWidth = colDef.width || colDef.minWidth || 200;
            const charsPerLine = Math.floor(columnWidth / 8);
            const lines = Math.ceil(value.length / charsPerLine);
            const estimatedHeight = 16 + (lines * 19);
            maxHeight = Math.max(maxHeight, estimatedHeight);
          }
        }
      });
      
      return Math.min(maxHeight, 300);
    };
    
    const gridOptions = {
      columnDefs: columnDefs as any,
      rowData,
      domLayout: domLayout as 'normal' | 'autoHeight',
      suppressCellFocus: false,
      stopEditingWhenCellsLoseFocus: true,
      headerHeight: showColumnKeys ? 40 : 32,
      getRowHeight: getRowHeight, // Use getRowHeight for both layouts to handle multiline cells properly
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      suppressContextMenu: true,
      suppressMovableColumns: true,
      // Allow popup editors (like rich select) to escape grid bounds
      popupParent: document.body,
      getRowId: (params: any) => {
        const id = params.data?.id || params.node?.id;
        if (!id) {
          logWarning('ReferenceGrid: getRowId returned undefined for row', "ReferenceGrid", undefined, { data: params.data });
          return `row_${params.node?.rowIndex || 0}`;
        }
        return String(id);
      },
      rowSelection: enableRowSelection ? {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true
      } : undefined,
      // Configure selection column to be pinned left (first column)
      selectionColumnDef: enableRowSelection ? {
        pinned: 'left',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        resizable: false,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        lockPosition: true
      } : undefined,
      components: {
        columnHeaderForTarget: ColumnHeaderForTarget
      },
      defaultColDef: {
        editable: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true
      },
      onGridReady: (params: any) => {
        gridApi = params.api;
        // gridInitialized is already set in reactive statement before initializeGrid is called
        // Initialize content hash after grid is ready (for rows/columns mode)
        if (rows.length > 0 && columns.length > 0 && !data2D && lastRowsContentHash === '') {
          lastRowsContentHash = createRowsContentHash(rows, columns);
          lastRowsLength = rows.length;
        }
        
        // Force a size calculation to ensure grid displays properly
        if (params.api) {
          if (domLayout === 'normal') {
            // For normal layout, size columns and force grid to resize
            setTimeout(() => {
              try {
                // Force grid to recalculate size
                params.api.sizeColumnsToFit();
                // Ensure grid is visible
                if (gridDiv && gridDiv.offsetHeight === 0) {
                  logWarning('ReferenceGrid: Grid div has zero height, parent height:', "ReferenceGrid", undefined, { parentHeight: gridDiv.parentElement?.offsetHeight });
                }
              } catch (error) {
                logError(error, "ReferenceGrid", undefined, { message: 'ReferenceGrid: Error sizing columns' });
              }
            }, 100);
          } else {
            // For autoHeight layout, just auto-size columns
            setTimeout(() => {
              params.api.sizeColumnsToFit();
            }, 100);
          }
        }
        
        if (onGridReady) {
          onGridReady(params.api);
        }
      },
      onSelectionChanged: enableRowSelection ? (params: any) => {
        const selectedNodes = params.api.getSelectedNodes();
        const newSelectedRows = new Set<number>();
        
        selectedNodes.forEach((node: any) => {
          if (node.rowIndex !== null && node.rowIndex !== undefined) {
            newSelectedRows.add(node.rowIndex);
          }
        });
        
        if (onSelectionChanged) {
          onSelectionChanged(newSelectedRows);
        }
      } : undefined,
      onCellValueChanged: (params: any) => {
        // ALWAYS call the default handler first to update the store and save
        // This ensures data is always persisted, regardless of whether a custom callback is provided
        handleDefaultCellValueChanged(params);
        
        // Then call the custom callback if provided (for additional processing like refreshing data2D)
        if (onCellValueChanged) {
          onCellValueChanged(params);
        }
      }
    };
    
    gridInstance = createGrid(gridDiv, gridOptions as any);
  }
  
  function handleDefaultCellValueChanged(params: any) {
    if (!$studyExtractStore || !referenceId || !$projectStructureStore) {
      logWarning('Cannot update reference cell - missing required data', "ReferenceGrid", undefined, {
        hasStore: !!$studyExtractStore,
        referenceId,
        hasProjectStructure: !!$projectStructureStore
      });
      return;
    }
    
    const { rowIndex, colDef, newValue, data } = params;
    const rowId = data?.id;
    if (!rowId) {
      logWarning(`Cannot find row ID for rowIndex ${rowIndex}`, "ReferenceGrid", undefined, { data });
      return;
    }
    
    const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
    if (!referenceStructure) {
      logWarning(`Reference not found: ${referenceId}`, "ReferenceGrid", undefined, {
        availableReferences: $projectStructureStore.references?.map((r: any) => r.id)
      });
      return;
    }
    
    // Find column by matching field or label
    const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
    let columnStructure = columns.find((col: any) => 
      col.id === colDef.field || 
      col.label === colDef.headerName ||
      col.label === colDef.field
    );
    
    // If not found, check if it's a field within a grouped column
    if (!columnStructure) {
      for (const col of columns) {
        if (col.type === 'group' && Array.isArray(col.fields)) {
          const field = col.fields.find((f: any) => f.id === colDef.field);
          if (field) {
            // Found the field within a grouped column - use the field structure
            columnStructure = field;
            break;
          }
        }
      }
    }
    
    if (!columnStructure) {
      logWarning(`Column not found for field: ${colDef.field}`, "ReferenceGrid", undefined, {
        field: colDef.field,
        headerName: colDef.headerName,
        availableColumns: columns.map((c: any) => ({ id: c.id, label: c.label, type: c.type }))
      });
      return;
    }
    
    // For grouped column fields, use the field ID directly (not the group column ID)
    const columnId = columnStructure.id || colDef.field;
    const valueToSave = newValue === '' || newValue === null || newValue === undefined ? '-' : newValue;
    
    const currentUser = $userStore?.user;
    const userId = currentUser?.user_id || currentUser?.oid || 'unknown';
    const userName = currentUser?.name || currentUser?.email || 'Unknown User';
    
    const updatedStudy = updateReferenceCell(
      $studyExtractStore,
      referenceId,
      rowId,
      columnId,
      valueToSave,
      userId,
      userName
    );
    
    // Verify the update worked
    const updatedReference = updatedStudy.references?.find((r: any) => r.id === referenceId);
    const updatedRow = updatedReference?.rows?.find((r: any) => r.id === rowId);
    const updatedCell = updatedRow?.cells?.find((c: any) => c.id === columnId);
    
    // Update grid row with new sourceInfo BEFORE updating store
    // This prevents the reactive statement from triggering a full reinit
    if (gridApi && params.node) {
      try {
        if (updatedCell?.sourceInfo) {
          // Update the row data with new value and sourceInfo
          const updatedRowData = { 
            ...data,
            id: rowId,
            [colDef.field]: valueToSave, // Update the cell value
            [`${columnId}_sourceInfo`]: updatedCell.sourceInfo // Update sourceInfo
          };
          
          // Use applyTransaction to update the row
          gridApi.applyTransaction({ update: [updatedRowData] });
          
          // Refresh only the affected cell to update indicator and tooltip
          gridApi.refreshCells({ 
            rowNodes: [params.node],
            columns: [colDef.field],
            force: true 
          });
          
        } else {
          // Even if no sourceInfo, update the value
          const updatedRowData = { 
            ...data,
            id: rowId,
            [colDef.field]: valueToSave
          };
          gridApi.applyTransaction({ update: [updatedRowData] });
        }
      } catch (error) {
        logError(error, "ReferenceGrid", undefined, { message: "Error updating grid row" });
      }
    } else {
      logWarning('ReferenceGrid: Cannot update grid - missing gridApi or node', "ReferenceGrid", undefined, {
        hasGridApi: !!gridApi,
        hasNode: !!params.node
      });
    }
    
    // Update store AFTER grid update to minimize reactive triggers
    studyExtractStore.set(updatedStudy);
    
    // Save to localStorage
    try {
      saveTargetExtract('study', 'study_1', updatedStudy);
    } catch (error) {
      logError(error, "ReferenceGrid", undefined, { message: "Error saving to localStorage" });
    }
  }
  
  // Reactive: reinitialize grid when props change
  // Only reinitialize if data structure changes, not on every store update
  $: if (gridDiv && (columns.length > 0 || (data2D && data2D.length > 0) || rows.length > 0)) {
    const hasColumns = columns.length > 0;
    const hasData2D = data2D && data2D.length > 0;
    const hasRows = rows.length > 0;
    const hasColumnStructure = columnStructure && columnStructure.length > 0;
    
    // Check if data structure actually changed (not just store reference)
    const columnsChanged = columns.length !== lastColumnsLength;
    const rowsChanged = rows.length !== lastRowsLength;
    const data2DChanged = (data2D?.length || 0) !== lastData2DLength;
    const columnStructureChanged = (columnStructure?.length || 0) !== lastColumnStructureLength;
    const referenceIdChanged = referenceId !== lastReferenceId;
    
    // Only reinitialize if structure actually changed or grid not initialized yet
    const structureChanged = columnsChanged || rowsChanged || data2DChanged || columnStructureChanged || referenceIdChanged || !gridInitialized;
    
    if (structureChanged && !gridInitialized) {
      
      // For data2D mode, we need both data2D and columnStructure (if provided)
      // For columns/rows mode, we need columns
      const canInitialize = (hasData2D && (hasColumnStructure || !columnStructure)) || (hasColumns && hasRows) || (hasData2D && data2D && data2D.length > 1);
      
      if (canInitialize) {
        // Set initialized flag BEFORE calling initializeGrid to prevent reactive loop
        gridInitialized = true;
        // Update tracking variables to prevent retriggering
        lastColumnsLength = columns.length;
        lastRowsLength = rows.length;
        lastData2DLength = data2D?.length || 0;
        lastColumnStructureLength = columnStructure?.length || 0;
        lastReferenceId = referenceId;
        // Initialize content hash for change detection (for rows/columns mode)
        if (rows.length > 0 && columns.length > 0 && !data2D) {
          lastRowsContentHash = createRowsContentHash(rows, columns);
        }
        
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          initializeGrid();
        }, 0);
      } else {
        logWarning('ReferenceGrid: No data available yet', "ReferenceGrid", undefined, {
          columnsLength: columns.length,
          rowsLength: rows.length,
          data2DLength: data2D?.length || 0,
          columnStructureLength: columnStructure?.length || 0,
          hasColumnStructure
        });
      }
    } else if (structureChanged && gridInitialized) {
      // Grid is initialized but structure changed - update tracking variables
      // and handle data updates without recreating grid
      if ((data2DChanged || rowsChanged) && gridApi && gridInstance) {
        try {
          const { columnDefs: newColumnDefs, rowData: newRowData } = createColumnDefsAndRowData();
          // Use setGridOption to replace all row data (better for add/remove operations)
          gridApi.setGridOption('rowData', newRowData);
          // Update tracking variables
          lastColumnsLength = columns.length;
          lastRowsLength = rows.length;
          lastData2DLength = data2D?.length || 0;
          lastColumnStructureLength = columnStructure?.length || 0;
          lastReferenceId = referenceId;
        } catch (error) {
          logError(error, "ReferenceGrid", undefined, { message: 'ReferenceGrid: Error updating grid data, will recreate' });
          // Reset and recreate
          gridInitialized = false;
        }
      } else {
        // Structure changed in a way that requires recreation (columns changed, etc.)
        gridInitialized = false;
        lastColumnsLength = columns.length;
        lastRowsLength = rows.length;
        lastData2DLength = data2D?.length || 0;
        lastColumnStructureLength = columnStructure?.length || 0;
        lastReferenceId = referenceId;
        // Will retrigger on next reactive cycle
      }
    }
  }
  
  // Helper function to create a content hash from rows (includes cell values)
  function createRowsContentHash(rows: any[], columns: any[]): string {
    if (!rows || rows.length === 0) return '';
    // Create a hash that includes row IDs, cell values, and sourceInfo
    const hashParts = rows.map((row: any) => {
      const rowParts = [row.id || ''];
      if (row.cells && columns.length > 0) {
        columns.forEach((col: any) => {
          // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
          if (col.type === 'group' && Array.isArray(col.fields)) {
            // For grouped columns, check cells by field IDs
            col.fields.forEach((field: any) => {
              const cell = row.cells.find((c: any) => c.id === field.id);
              if (cell) {
                rowParts.push(`${field.id}:${cell.value || ''}`);
                if (cell.sourceInfo) {
                  rowParts.push(`${field.id}_source:${cell.sourceInfo.sourceType || ''}:${cell.sourceInfo.sourceDate || ''}`);
                }
              }
            });
          } else {
            // Handle regular columns
            const cell = row.cells.find((c: any) => c.id === col.id);
            if (cell) {
              rowParts.push(`${col.id}:${cell.value || ''}`);
              if (cell.sourceInfo) {
                rowParts.push(`${col.id}_source:${cell.sourceInfo.sourceType || ''}:${cell.sourceInfo.sourceDate || ''}`);
              }
            }
          }
        });
      }
      return rowParts.join('|');
    });
    return hashParts.join('||');
  }
  
  let lastRowsContentHash = ''; // Track content hash to detect any changes (structure or values)
  
  // Additional reactive statement to handle rows changes when using rows prop (ExtractItems2 mode)
  // This ensures that when rows are added/removed OR cell values change, the grid updates
  $: if (gridApi && gridInitialized && rows && columns.length > 0 && !data2D) {
    // Only run this for rows/columns mode (not data2D mode)
    // Create a content hash that includes row IDs, cell values, and sourceInfo
    const currentContentHash = createRowsContentHash(rows, columns);
    const rowsLength = rows.length || 0;
    
    // Update if content hash changed (detects both structure changes AND cell value changes)
    // But skip on first run (lastRowsContentHash is empty)
    if (currentContentHash !== lastRowsContentHash && lastRowsContentHash !== '') {
      try {
        const { rowData: newRowData } = createColumnDefsAndRowData();
        if (newRowData && Array.isArray(newRowData)) {
          // Use applyTransaction to update rows in place (avoids flashing)
          // This will update existing rows and add/remove as needed
          const currentRowData: any[] = [];
          gridApi.forEachNode((node: any) => {
            if (node.data) {
              currentRowData.push(node.data);
            }
          });
          
          // Compare current and new row data to determine what changed
          const rowIdsToUpdate = new Set<string>();
          const rowIdsToAdd = new Set<string>();
          const rowIdsToRemove = new Set<string>();
          
          // Find rows to update or add
          newRowData.forEach((newRow: any) => {
            const existingRow = currentRowData.find((r: any) => r.id === newRow.id);
            if (existingRow) {
              // Check if row content actually changed
              const existingHash = JSON.stringify(existingRow);
              const newHash = JSON.stringify(newRow);
              if (existingHash !== newHash) {
                rowIdsToUpdate.add(newRow.id);
              }
            } else {
              rowIdsToAdd.add(newRow.id);
            }
          });
          
          // Find rows to remove
          currentRowData.forEach((row: any) => {
            if (!newRowData.find((r: any) => r.id === row.id)) {
              rowIdsToRemove.add(row.id);
            }
          });
          
          // Apply changes
          const rowsToUpdate = newRowData.filter((r: any) => rowIdsToUpdate.has(r.id));
          const rowsToAdd = newRowData.filter((r: any) => rowIdsToAdd.has(r.id));
          const rowsToRemove = currentRowData.filter((r: any) => rowIdsToRemove.has(r.id));
          
          if (rowsToUpdate.length > 0 || rowsToAdd.length > 0 || rowsToRemove.length > 0) {
            if (gridApi) {
              const api = gridApi;
              api.applyTransaction({
                update: rowsToUpdate,
                add: rowsToAdd,
                remove: rowsToRemove
              });
              // Refresh updated rows to ensure indicators and tooltips are updated
              if (rowsToUpdate.length > 0) {
                const nodesToRefresh = rowsToUpdate
                  .map((row: any) => api.getRowNode(row.id))
                  .filter((node): node is RowNode<any> => !!node);
                if (nodesToRefresh.length > 0) {
                  api.refreshCells({ rowNodes: nodesToRefresh, force: true });
                }
              }
            }
            lastRowsContentHash = currentContentHash;
            lastRowsLength = rowsLength;
          } else {
            // Fallback: if no changes detected but hash changed, refresh all
            if (gridApi) {
              const api = gridApi;
              api.setGridOption('rowData', newRowData);
            }
            lastRowsContentHash = currentContentHash;
            lastRowsLength = rowsLength;
          }
        } else {
          logWarning('ReferenceGrid: createColumnDefsAndRowData returned invalid data:', "ReferenceGrid", undefined, { data: newRowData });
        }
      } catch (error) {
        logError(error, "ReferenceGrid", undefined, { message: 'ReferenceGrid: Error refreshing grid with new rows' });
        // Reset hash to force retry on next change
        lastRowsContentHash = '';
      }
    } else if (lastRowsContentHash === '' && rowsLength > 0) {
      // Initialize hash on first run
      lastRowsContentHash = currentContentHash;
      lastRowsLength = rowsLength;
    }
  }
  
  // Reactive: update header height when showColumnKeys changes
  $: if (gridApi && showColumnKeys !== undefined) {
    gridApi.setGridOption('headerHeight', showColumnKeys ? 40 : 32);
    // Refresh headers to show/hide keys
    gridApi.refreshHeader();
  }
  
  // Reactive: refresh cells when showIndicators changes (same as TopicGrid)
  // Explicitly reference showIndicators in reactive statement to ensure it tracks changes
  $: if (gridApi && showIndicators !== undefined) {
    // Force reactive tracking by accessing showIndicators value
    const currentShowIndicators = showIndicators;
    // Refresh all cells to update indicators
    gridApi.refreshCells({ force: true });
  }
  
  onMount(() => {
    // The reactive statement should handle initialization
    // onMount is just a fallback in case reactive statement hasn't run yet
    // But we should check if already initialized to avoid double initialization
    if (gridDiv && !gridInitialized) {
      // Use setTimeout to ensure DOM is ready and reactive statement has had a chance to run
      setTimeout(() => {
        // Only initialize if reactive statement hasn't done it yet
        if (!gridInitialized && gridDiv) {
          const hasColumns = columns.length > 0;
          const hasData2D = data2D && data2D.length > 0;
          const hasRows = rows.length > 0;
          const hasColumnStructure = columnStructure && columnStructure.length > 0;
          const canInitialize = (hasData2D && (hasColumnStructure || !columnStructure)) || (hasColumns && hasRows) || (hasData2D && data2D && data2D.length > 1);
          
          if (canInitialize) {
            gridInitialized = true;
            initializeGrid();
          }
        }
      }, 50); // Small delay to let reactive statement run first
    }
  });
  
  onDestroy(() => {
    if (gridInstance) {
      gridInstance.destroy();
      gridInstance = null;
      gridApi = null;
      gridInitialized = false;
    }
  });
</script>

<div 
  bind:this={gridDiv}
  class="ag-theme-quartz ag-reference-grid"
  class:ag-reference-grid-scrollable={domLayout === 'normal'}
  data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
></div>

